import { useQuery } from '@tanstack/react-query';
import locationService, {
  ApiResponse, 
  PaginatedResponse 
} from '../services/locationService';
import { useMemo } from 'react';

// Hook for fetching countries
export const useCountries = (params?: {
  name?: string;
  code?: string;
  with_states?: boolean;
  with_cities?: boolean;
  page?: number;
  per_page?: number;
  search?: string;
  select_country?: number;
}) => {
  // Create a deep copy of params to ensure it doesn't trigger unnecessary re-renders
  const stableParams = useMemo(() => {
    if (!params) return undefined;
    return {...params};
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    params?.name, 
    params?.code, 
    params?.with_states, 
    params?.with_cities, 
    params?.page, 
    params?.per_page, 
    params?.search,
    params?.select_country
  ]);

  // Extract specific keys for the queryKey
  const queryKeys = useMemo(() => ({
    search: params?.search || '',
    page: params?.page || 1,
    per_page: params?.per_page || 15,
    select_country: params?.select_country || 0
  }), [params?.search, params?.page, params?.per_page, params?.select_country]);

  // Use shorter staleTime when searching to ensure fresh data
  // Only consider it "searching" if search is defined and not empty
  const isSearching = !!(params?.search && params.search.trim() !== '');
  const staleTime = isSearching ? 0 : 60 * 60 * 1000; // 0 when searching (always refetch), 1 hour otherwise

  return useQuery({
    // Make sure search, page and per_page are in the key directly
    queryKey: ['countries', queryKeys],
    queryFn: () => {
      return locationService.getCountries(stableParams);
    },
    staleTime,
    refetchOnMount: isSearching, // Force refetch when searching
    refetchOnWindowFocus: false,
  });
};

// Hook for fetching a single country
export const useCountry = (id: number | undefined, params?: {
  with_states?: boolean;
  with_cities?: boolean;
}) => {
  return useQuery({
    queryKey: ['country', id, params],
    queryFn: () => locationService.getCountry(id!, params),
    enabled: !!id,
    staleTime: 1000 * 60 * 60, // 1 hour
  });
};

// Hook for fetching states by country
export const useStatesByCountry = (countryId: number | undefined, params?: {
  name?: string;
  code?: string;
  with_cities?: boolean;
  page?: number;
  per_page?: number;
  search?: string;
  select_state?: number;
}) => {
  // Create a deep copy of params to ensure it doesn't trigger unnecessary re-renders
  const stableParams = useMemo(() => {
    if (!params) return undefined;
    return {...params, country_id: countryId};
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    countryId,
    params?.name, 
    params?.code, 
    params?.with_cities, 
    params?.page, 
    params?.per_page, 
    params?.search,
    params?.select_state
  ]);

  // Extract specific keys for the queryKey
  const queryKeys = useMemo(() => ({
    countryId: countryId || 0,
    search: params?.search || '',
    page: params?.page || 1,
    per_page: params?.per_page || 15,
    select_state: params?.select_state || 0
  }), [countryId, params?.search, params?.page, params?.per_page, params?.select_state]);

  // Use shorter staleTime when searching to ensure fresh data
  // Only consider it "searching" if search is defined and not empty
  const isSearching = !!(params?.search && params.search.trim() !== '');
  const staleTime = isSearching ? 0 : 60 * 60 * 1000; // 0 when searching (always refetch), 1 hour otherwise

  return useQuery({
    // Make sure search, page and per_page are in the key directly
    queryKey: ['states', queryKeys],
    queryFn: () => {
      return locationService.getStates(stableParams);
    },
    enabled: !!countryId,
    staleTime,
    refetchOnMount: isSearching, // Force refetch when searching
    refetchOnWindowFocus: false,
  });
};

// Hook for fetching a single state
export const useState = (id: number | undefined, params?: {
  with_cities?: boolean;
}) => {
  return useQuery({
    queryKey: ['state', id, params],
    queryFn: () => locationService.getState(id!, params),
    enabled: !!id,
    staleTime: 1000 * 60 * 60, // 1 hour
  });
};

// Hook for fetching cities by state
export const useCitiesByState = (stateId: number | undefined, params?: {
  name?: string;
  page?: number;
  per_page?: number;
  search?: string;
  select_city?: number;
}) => {
  // Create a deep copy of params to ensure it doesn't trigger unnecessary re-renders
  const stableParams = useMemo(() => {
    if (!params) return undefined;
    return {...params, state_id: stateId};
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    stateId,
    params?.name, 
    params?.page, 
    params?.per_page, 
    params?.search,
    params?.select_city
  ]);

  // Extract specific keys for the queryKey
  const queryKeys = useMemo(() => ({
    stateId: stateId || 0,
    search: params?.search || '',
    page: params?.page || 1,
    per_page: params?.per_page || 15,
    select_city: params?.select_city || 0
  }), [stateId, params?.search, params?.page, params?.per_page, params?.select_city]);

  // Use shorter staleTime when searching to ensure fresh data
  // Only consider it "searching" if search is defined and not empty
  const isSearching = !!(params?.search && params.search.trim() !== '');
  const staleTime = isSearching ? 0 : 60 * 60 * 1000; // 0 when searching (always refetch), 1 hour otherwise

  return useQuery({
    // Make sure search, page and per_page are in the key directly
    queryKey: ['cities', queryKeys],
    queryFn: () => {
      return locationService.getCities(stableParams);
    },
    enabled: !!stateId,
    staleTime,
    refetchOnMount: isSearching, // Force refetch when searching
    refetchOnWindowFocus: false,
  });
};

// Hook for fetching a single city
export const useCity = (id: number | undefined) => {
  return useQuery({
    queryKey: ['city', id],
    queryFn: () => locationService.getCity(id!),
    enabled: !!id,
    staleTime: 1000 * 60 * 60, // 1 hour
  });
};

// Helper function to extract data from API response
export const extractData = <T,>(response?: ApiResponse<PaginatedResponse<T>> | any) => {
  if (!response) {
    return [];
  }

  // Handle direct array response
  if (Array.isArray(response)) {
    return response;
  }
  
  // Handle different object structures
  if (response.data) {
    // Format with data property
    if (Array.isArray(response.data)) {
      return response.data;
    }
    
    // Format with nested data.data (paginated response)
    if (response.data.data && Array.isArray(response.data.data)) {
      return response.data.data;
    }
    
    // If data is an object with items/results array
    if (response.data.items && Array.isArray(response.data.items)) {
      return response.data.items;
    }
    
    if (response.data.results && Array.isArray(response.data.results)) {
      return response.data.results;
    }
  }
  
  return [];
};

export const extractSingleData = <T,>(response?: ApiResponse<T>) => {
  return response?.data || null;
};

// Debug function to test API connectivity
export const testApiConnectivity = async () => {
  try {
    const response = await locationService.getCountries();
    return { success: true, data: response };
  } catch (error) {
    console.error('API Connectivity Test Failed:', error);
    return { success: false, error };
  }
};

// Helper function for local filtering when backend search is not needed
export const filterDataLocally = <T extends { name: string }>(data: T[], searchTerm?: string): T[] => {
  if (!searchTerm || searchTerm.trim() === '') {
    return data;
  }
  
  const normalizedSearchTerm = searchTerm.toLowerCase().trim();
  
  return data.filter(item => 
    item.name.toLowerCase().includes(normalizedSearchTerm)
  );
};

// Check if we should use backend search or local filtering
export const shouldUseBackendSearch = (
  totalItems: number, 
  perPage: number = 15
): boolean => {
  return totalItems > perPage;
}; 